import { useState, useRef, useEffect } from 'react';
import { AylikVeri } from '../utils/raporHesapla';

interface Props {
  veriler: AylikVeri[];
}

export default function KazancGrafigi({ veriler }: Props) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; veri: AylikVeri } | null>(null);
  const [containerW, setContainerW] = useState(600);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      for (const e of entries) setContainerW(e.contentRect.width);
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const W = containerW;
  const H = 220;
  const padL = 52;
  const padR = 16;
  const padT = 16;
  const padB = 32;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const maxVal = Math.max(...veriler.map(v => v.toplamKazanc), 1);
  const stepCount = 4;

  const toX = (i: number) => padL + (i / (veriler.length - 1)) * chartW;
  const toY = (v: number) => padT + chartH - (v / maxVal) * chartH;

  const linePath = veriler
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v.toplamKazanc).toFixed(1)}`)
    .join(' ');

  const areaPath =
    linePath +
    ` L${toX(veriler.length - 1).toFixed(1)},${(padT + chartH).toFixed(1)}` +
    ` L${toX(0).toFixed(1)},${(padT + chartH).toFixed(1)} Z`;

  const gridLines = Array.from({ length: stepCount + 1 }, (_, i) => {
    const val = Math.round((maxVal / stepCount) * i);
    const y = toY(val);
    return { val, y };
  });

  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Son 12 Ay Kazanç Trendi</h3>
          <p className="text-xs text-gray-400 mt-0.5">Aylık toplam gelir (₺)</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-1 bg-sky-500 rounded-full inline-block"></span>
          <span className="text-xs text-gray-500">Kazanç</span>
        </div>
      </div>

      <div ref={containerRef} className="relative w-full" style={{ height: H }}>
        <svg width={W} height={H} className="overflow-visible">
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.01" />
            </linearGradient>
          </defs>

          {/* Grid yatay çizgiler */}
          {gridLines.map(({ val, y }) => (
            <g key={val}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#f1f5f9" strokeWidth="1" />
              <text x={padL - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">
                {val >= 1000 ? `${(val / 1000).toFixed(0)}K` : val}
              </text>
            </g>
          ))}

          {/* Alan dolgusu */}
          <path d={areaPath} fill="url(#areaGrad)" />

          {/* Çizgi */}
          <path d={linePath} fill="none" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Çubuk hover alanları */}
          {veriler.map((v, i) => (
            <rect
              key={i}
              x={toX(i) - chartW / (veriler.length * 2)}
              y={padT}
              width={chartW / veriler.length}
              height={chartH}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={e => {
                setActiveIdx(i);
                const rect = (e.currentTarget as SVGRectElement).getBoundingClientRect();
                const containerRect = containerRef.current?.getBoundingClientRect();
                if (containerRect) {
                  setTooltip({
                    x: rect.left - containerRect.left + rect.width / 2,
                    y: toY(v.toplamKazanc) - 8,
                    veri: v,
                  });
                }
              }}
              onMouseLeave={() => { setActiveIdx(null); setTooltip(null); }}
            />
          ))}

          {/* Noktalar */}
          {veriler.map((v, i) => {
            const isActive = activeIdx === i;
            const cx = toX(i);
            const cy = toY(v.toplamKazanc);
            return (
              <circle
                key={i} cx={cx} cy={cy}
                r={isActive ? 5 : v.toplamKazanc > 0 ? 3.5 : 0}
                fill={isActive ? '#0284c7' : '#0ea5e9'}
                stroke="white" strokeWidth="2"
                style={{ transition: 'r 0.15s' }}
              />
            );
          })}

          {/* X ekseni etiketleri */}
          {veriler.map((v, i) => (
            <text
              key={i}
              x={toX(i)} y={H - 4}
              textAnchor="middle" fontSize="10" fill="#94a3b8"
            >
              {v.ay}
            </text>
          ))}
        </svg>

        {/* Tooltip */}
        {tooltip && (
          <div
            className="absolute z-10 pointer-events-none bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg"
            style={{
              left: tooltip.x,
              top: tooltip.y - 48,
              transform: 'translateX(-50%)',
            }}
          >
            <p className="font-bold">{tooltip.veri.ay} {tooltip.veri.yil}</p>
            <p className="text-gray-300">{tooltip.veri.servisSayisi} servis</p>
            <p className="text-sky-300 font-semibold">₺{tooltip.veri.toplamKazanc.toLocaleString('tr-TR')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
